import React, { FunctionComponent, useEffect } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import { Link, Text } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import {
  setConfirmTermsViewed,
  useAppDispatch,
  useAppRouter,
  useConsumerAmount,
  useIsAfterpayAppGiftCardFlow,
  useLoanAgreementDocumentId,
  usePaymentAuthorisationDocumentId,
} from '../../state'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useMoney } from '../Money/useMoney'
import { useGetIsAutoPayAgreed } from '../AutopayToggle/hooks'
import { buildTranslationKey, TranslationSegment } from '../../utils/locales'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'

interface TermsProps {
  setDocumentIdToShow: (documentId: string, isTilDisclosure?: boolean) => void
}

export const Terms: FunctionComponent<TermsProps> = ({ setDocumentIdToShow }) => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const { extraSmallTextSize } = useCheckoutSizes()
  const autopayAgreed = useGetIsAutoPayAgreed()
  const paymentAuthorisationDocumentId = usePaymentAuthorisationDocumentId()
  const loanAgreementDocumentId = useLoanAgreementDocumentId()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const estimatedCartAmount = useConsumerAmount()
  const dispatch = useAppDispatch()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const { formatMoney } = useMoney({
    locale,
    currency: estimatedCartAmount?.currency,
  })
  const context = autopayAgreed ? 'autopayOn' : 'autopayOff'

  const components = {
    autoPayAuthorization: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='auto-payment-authorisation-link'
        key='til-autopay-authorisation'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_AFF_TRUTH_IN_LENDING_PAGE_PAYMENT_AUTHORISATION_LINK)
          setDocumentIdToShow(paymentAuthorisationDocumentId)
        }}
      />
    ),
    truthInLendingDisclosure: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='payment-authorisation-link'
        key='til-disclosure'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_AFF_TRUTH_IN_LENDING_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK)
          setDocumentIdToShow(loanAgreementDocumentId, true)
        }}
      />
    ),
    loanAgreement: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='loan-agreement-link'
        key='til-loan-agreement'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_AFF_TRUTH_IN_LENDING_PAGE_LOAN_AGREEMENT_LINK)
          setDocumentIdToShow(loanAgreementDocumentId)
        }}
      />
    ),
    FEBPolicy: (
      <Link
        kind='Underline'
        color='Orca'
        testNameSpace='feb-privacy-policy-link'
        key='til-feb-privacy-policy'
        href={t('financeFee:til.febPrivacyPolicyUrl')}
        target='_blank'
        rel='noopener noreferrer'
      />
    ),
    br: <br />,
  }

  const termSegment: TranslationSegment = {
    contexts: [[isAfterpayAppGiftCardFlow, 'terms_giftCard']],
    default: 'terms',
  }

  const i18nKey = buildTranslationKey({ namespace: 'financeFee', segments: ['til', termSegment] })

  useEffect(() => {
    const termsKey = `${i18nKey}_${context}`
    dispatch(setConfirmTermsViewed(termsKey))
  }, [i18nKey, context])

  return (
    <>
      <MaskedElement actionName='Terms And Conditions'>
        <Text color='Gray40' size={extraSmallTextSize} testNameSpace='terms-conditions'>
          <Trans
            i18nKey={i18nKey}
            components={components}
            context={context}
            values={{
              amount: formatMoney(estimatedCartAmount?.amount),
            }}
          />
        </Text>
      </MaskedElement>
      <TrackOnMount eventName={TrackingEvent.VIEWED_CHECKOUT_AFF_TRUTH_IN_LENDING_TERMS} eventProperties={{ autopayAgreed, termsViewed: i18nKey }} />
    </>
  )
}
